<template>
  <div class="mls-tab-wrap">
    <div ref="table"></div>
  </div>
</template>

<script>
import { TabulatorFull as Tabulator } from 'tabulator-tables';
import { wrapColumns } from "../../utilities/tabulatorSorter";
import { listingTypePillHtml } from "../../composables/listingTypePill";
import 'tabulator-tables/dist/css/tabulator_bootstrap4.min.css';

const PILL_COLORS = {
  'Now Selling': { bg: 'rgba(31,157,85,0.10)', fg: '#1F7A45', dot: '#1F9D55' },
  'Now Leasing': { bg: 'rgba(31,157,85,0.10)', fg: '#1F7A45', dot: '#1F9D55' },
  'Selling Soon': { bg: 'rgba(255,184,28,0.14)', fg: '#8A6A00', dot: '#FFB81C' },
  'Leasing Soon': { bg: 'rgba(255,184,28,0.14)', fg: '#8A6A00', dot: '#FFB81C' },
  'In Planning': { bg: 'rgba(52,51,118,0.10)', fg: '#343376', dot: '#343376' },
  'Sold Out': { bg: 'rgba(84,84,84,0.10)', fg: '#545454', dot: '#8A8A8A' },
  'Leased Out': { bg: 'rgba(84,84,84,0.10)', fg: '#545454', dot: '#8A8A8A' },
  Active: { bg: 'rgba(31,157,85,0.10)', fg: '#1F7A45', dot: '#1F9D55' },
  Sold: { bg: 'rgba(212,56,71,0.10)', fg: '#A8323F', dot: '#D43847' },
  Leased: { bg: 'rgba(31,157,85,0.10)', fg: '#1F7A45', dot: '#1F9D55' },
};


// Maps a Tabulator column field to a whitelisted sort key on the paged
// endpoint. Columns absent here fall back to the endpoint's default sort.
const SORT_KEY = {
  name: 'project',
  developer: 'developer',
  customLocationData: 'location',
  field_mls_address_value: 'unit_number',
  custom_field_mls_beds_value: 'type',
  field_mls_baths_value: 'baths',
  field_mls_unit_size_value: 'sqft',
  field_mls_list_price_value: 'price',
  psf: 'psf',
  net_price: 'net_price',
  custom_net_psf: 'net_price',
  price_date: 'price_date',
  status_name_name: 'status',
};

function pill(val) {
  const c = PILL_COLORS[val] || { bg: 'rgba(84,84,84,0.10)', fg: '#545454', dot: '#8A8A8A' };
  return `<span class="rpd-pill" style="background:${c.bg};color:${c.fg}"><span class="rpd-pill-dot" style="background:${c.dot}"></span>${val || '-'}</span>`;
}

function verifiedBadge(type) {
  if (type === 'Verified')
    return `<span class="rpd-vbadge rpd-vbadge--green" title="Verified">&#10003;</span>`;
  if (type === 'Semi-Verified')
    return `<span class="rpd-vbadge rpd-vbadge--amber" title="Semi-Verified">&#126;</span>`;
  return `<span class="rpd-vbadge rpd-vbadge--empty"></span>`;
}

function fmtPrice(v) {
  if (!v || v === '-' || v === 0 || v === '0') return '-';
  const n = Number(typeof v === 'string' ? v.replace(/[$,]/g, '') : v);
  if (!n || isNaN(n)) return '-';
  if (n >= 1_000_000) return `$${(n / 1_000_000).toFixed(2)}M`;
  return `$${Math.round(n).toLocaleString()}`;
}

function pctChip(curr, prev) {
  if (!prev || !curr || Number(prev) === 0 || isNaN(Number(prev)) || isNaN(Number(curr))) return '';
  const pct = ((Number(curr) - Number(prev)) / Number(prev)) * 100;
  if (Math.abs(pct) < 0.01) return '';
  const up = pct > 0;
  const cls = up ? 'rpd-chip--up' : 'rpd-chip--dn';
  return `<span class="rpd-chip ${cls}">${up ? '&#9650;' : '&#9660;'}${Math.abs(pct).toFixed(2)}%</span>`;
}

export default {
  name: 'MlsReportTable',

  props: {
    rowData: { type: Array, default: () => [] },
    isMarketStrata: { type: Boolean, default: true },
    fields: { type: Array, default: null },
    // When true the table streams pages from serverUrl (infinite scroll +
    // remote sort). When false it renders the in-memory rowData array.
    serverMode: { type: Boolean, default: false },
    serverUrl: { type: String, default: '' },
    // Base filter query string for the paged endpoint; must end with "&".
    serverQuery: { type: String, default: '' },
    // Enriches a raw endpoint row into the shape the formatters expect.
    rowMapper: { type: Function, default: null },
  },

  data() {
    return { tabulator: null, tableBuilt: false };
  },

  mounted() {
    this.initTable();
  },

  beforeDestroy() {
    if (this._scrollHolder && this._onScroll) {
      this._scrollHolder.removeEventListener('scroll', this._onScroll);
    }
    if (this.tabulator) this.tabulator.destroy();
    // A late watcher or history callback must not reach a destroyed instance.
    this.tabulator = null;
    this.tableBuilt = false;
  },

  watch: {
    // Switching data source (server <-> local) requires a fresh Tabulator.
    serverMode() {
      this.rebuildTable();
    },
    rowData(newData, oldData) {
      if (!this.tabulator || this.serverMode) return;
      // Setting data before the build finishes throws in Tabulator 6; replay
      // the latest array from the tableBuilt handler instead.
      if (!this.tableBuilt) {
        this._dataDirty = true;
        return;
      }
      const nd = newData || [];
      const od = oldData || [];
      // Infinite scroll append: the new array extends the old one at the tail
      // (same first + last existing rows, more rows after). Add ONLY the new
      // rows via addData so scroll position is kept — no full-table refresh.
      const isAppend =
        od.length > 0 && nd.length > od.length &&
        nd[0] && od[0] && nd[0].entity_id === od[0].entity_id &&
        nd[od.length - 1] && od[od.length - 1] &&
        nd[od.length - 1].entity_id === od[od.length - 1].entity_id;
      if (isAppend) {
        this.tabulator.addData(nd.slice(od.length));
        this._reachedEnd = false; // let the next bottom-hit fire again
      } else {
        this.tabulator.replaceData(nd);
      }
    },
    // In server mode a filter/param change means reload the single page.
    serverQuery() {
      this.loadServer();
    },
    fields() {
      // The build is async and the parent sets its field layout in mounted(),
      // so a change can land while the header doesn't exist yet. Defer to the
      // tableBuilt handler rather than reaching into a half-built table.
      if (!this.tabulator || !this.tableBuilt) {
        this._columnsDirty = true;
        return;
      }
      this.setColumns(this.resolvedFields);
    },
  },

  computed: {
    resolvedFields() {
      return this.fields && this.fields.length
        ? this.fields
        : ['project', 'location', 'unit', 'type', 'baths', 'size', 'listPrice', 'psf', 'incentives', 'netPrice', 'netPsf', 'priceDate', 'unitStatus', 'projectStatus', 'listing'];
    },
  },

  methods: {
    colDef(id) {
      const vm = this;
      const defs = {
        project: {
          title: 'Project & Developer',
          field: 'name',
          minWidth: 200,
          frozen: true,
          formatter(cell) {
            const row = cell.getRow().getData();
            const badge = verifiedBadge(row.price_backup);
            // customData is absent on rows that skipped enrichment; fall back to
            // a plain label rather than throwing inside the formatter.
            const href = (row.customData && row.customData.href_link) || '';
            const nameEl = href
              ? `<a href="${href}" target="_blank" class="rpd-proj-name">${row.name || '-'}</a>`
              : `<span class="rpd-proj-name">${row.name || '-'}</span>`;
            return `${nameEl}<br/><span class="rpd-proj-dev">${row.developer || ''}</span>`;
          },
        },
        location: {
          title: 'Location',
          field: 'customLocationData',
          minWidth: 160,
          sorter(a, b) {
            return String(a?.address || '').localeCompare(String(b?.address || ''));
          },
          formatter(cell) {
            const v = cell.getValue() || {};
            return `<div class="rpd-loc-cell"><span class="rpd-loc-addr">${v.address || '-'}</span><span class="rpd-loc-city">${v.municipality || v.neighbour || ''}</span></div>`;
          },
        },
        unit: {
          title: 'Unit #',
          field: 'field_mls_address_value',
          width: 70,
          formatter: cell => {
            const v = cell.getValue();
            return v ? `<span class="rpd-unit-num">#${v}</span>` : '-';
          },
        },
        planName: {
          title: 'Plan Name',
          field: 'field_mls_plan_name_value',
          width: 120,
        },
        type: {
          title: 'Type',
          field: 'custom_field_mls_beds_value',
          width: 110,
          sorter(a, b) {
            return String(a?.type || a || '').localeCompare(String(b?.type || b || ''));
          },
          formatter(cell) {
            const v = cell.getValue();
            if (!v) return '-';
            const type = typeof v === 'object' ? (v.type || '-') : String(v);
            const fp = typeof v === 'object' && v.fp_specs && v.fp_specs !== 'None'
              ? ` <span class="rpd-fp-spec">${v.fp_specs}</span>` : '';
            return `<span class="rpd-type">${type}</span>${fp}`;
          },
        },
        baths: {
          title: 'Ba',
          field: 'field_mls_baths_value',
          width: 48,
          hozAlign: 'right',
        },
        size: {
          title: 'Size',
          field: 'field_mls_unit_size_value',
          width: 80,
          hozAlign: 'right',
          sorter: 'number',
          formatter(cell) {
            const n = Number(cell.getValue());
            return (n && !isNaN(n)) ? `${Math.round(n).toLocaleString()}<span class="rpd-sf"> sf</span>` : '-';
          },
        },
        listPrice: {
          title: 'List Price',
          field: 'field_mls_list_price_value',
          width: 160,
          hozAlign: 'right',
          sorter: 'number',
          formatter(cell) {
            const row = cell.getRow().getData();
            const chip = pctChip(cell.getValue(), row.price_second);
            return `<div class="rpd-price-cell"><span class="rpd-price">${fmtPrice(cell.getValue())}</span>${chip}</div>`;
          },
        },
        psf: {
          title: '$PSF',
          field: 'psf',
          width: 80,
          hozAlign: 'right',
          sorter: 'number',
          formatter(cell) {
            const v = cell.getValue();
            if (!v || v === '-') return '-';
            const n = Number(typeof v === 'string' ? v.replace(/[$,]/g, '') : v);
            if (!n || isNaN(n)) return '-';
            return vm.isMarketStrata
              ? `$${Math.round(n).toLocaleString()}`
              : `$${n.toFixed(2)}`;
          },
        },
        incentives: {
          title: 'Incentives',
          field: 'buyer_incentives',
          width: 110,
          formatter(cell) {
            const v = cell.getValue();
            if (!v || v === '-') return `<span class="rpd-nil">&#8212;</span>`;
            const n = Number(typeof v === 'string' ? v.replace(/[$,]/g, '') : v);
            if (!n || isNaN(n)) return `<span class="rpd-nil">&#8212;</span>`;
            return `<span class="rpd-incent">$${Math.round(n).toLocaleString()}</span>`;
          },
        },
        netPrice: {
          title: 'Net Price',
          field: 'net_price',
          width: 110,
          hozAlign: 'right',
          sorter: 'number',
          formatter: cell => {
            const v = cell.getValue();
            return (v && v !== '-') ? `<span class="rpd-net">${fmtPrice(v)}</span>` : '-';
          },
        },
        netPsf: {
          title: 'Net $PSF',
          field: 'custom_net_psf',
          width: 80,
          hozAlign: 'right',
          sorter: 'number',
          formatter: cell => {
            const v = cell.getValue();
            return (v && v !== '-') ? `$${v}` : '-';
          },
        },
        priceDate: {
          title: 'Price Date',
          field: 'price_date',
          width: 96,
          sorter: 'date',
        },
        unitStatus: {
          title: 'Unit Status',
          field: 'status_name_name',
          width: 100,
          formatter: cell => pill(cell.getValue()),
        },
        projectStatus: {
          title: 'Project Status',
          field: 'status',
          width: 120,
          formatter: cell => pill(cell.getValue()),
        },
        age: {
          title: 'Age',
          field: 'custom_age',
          width: 60,
          hozAlign: 'right',
          sorter: 'number',
          formatter(cell) {
            const v = cell.getValue();
            if (v === 0 || v === '0') return `<span class="rpd-new-badge">New</span>`;
            const n = Number(v);
            if (!n || isNaN(n) || v === '-') return '-';
            const col = n > 30 ? '#A8323F' : '#545454';
            return `<span style="color:${col}">${n}<span class="rpd-sf"> d</span></span>`;
          },
        },
        listing: {
          title: 'Listing',
          field: 'custom_listing_type',
          // Wide enough for the longest pill ("ASSIGNMENT") without clipping.
          width: 126,
          formatter(cell) {
            return listingTypePillHtml(cell.getValue());
          },
        },

        // ── Investor Rental extras ──────────────────────────────────────────
        // Reached only via the Investor Rental field set (MLSList.INVESTOR_FIELDS);
        // the strata Unit Report never requests them.
        soldDate: {
          title: 'Leased Date',
          field: 'sold_date',
          width: 100,
          sorter: 'date',
          formatter: cell => {
            const v = cell.getValue();
            return (v && v !== '-') ? v : `<span class="rpd-nil">&#8212;</span>`;
          },
        },
        viewOrientation: {
          title: 'View',
          field: 'view_orientation',
          width: 90,
          formatter: cell => cell.getValue() || '-',
        },
        balcony: {
          title: 'Balcony',
          field: 'balcony',
          width: 90,
          formatter: cell => cell.getValue() || '-',
        },
        balconyArea: {
          title: 'Balcony Area',
          field: 'balcony_area',
          width: 100,
          hozAlign: 'right',
          sorter: 'number',
          formatter(cell) {
            const n = Number(cell.getValue());
            return (n && !isNaN(n)) ? `${Math.round(n).toLocaleString()}<span class="rpd-sf"> sf</span>` : '-';
          },
        },
        parking: {
          title: 'Parking',
          field: 'parking',
          width: 90,
          formatter: cell => cell.getValue() || '-',
        },
        stalls: {
          title: 'Stalls',
          field: 'stalls',
          width: 70,
          hozAlign: 'right',
          formatter: cell => cell.getValue() || '-',
        },
      };
      return defs[id] || null;
    },

    setColumns(fieldIds) {
      if (!this.tabulator || !this.tableBuilt) return;
      const checkboxCol = {
        title: '',
        field: 'entity_id',
        headerSort: false,
        width: 36,
        frozen: true,
        formatter: () => `<input type="checkbox" style="cursor:pointer;accent-color:#12283F;">`,
      };
      this.tabulator.setColumns([checkboxCol, ...fieldIds.map(id => this.colDef(id)).filter(Boolean)]);
    },

    buildColumns() {
      const vm = this;
      return wrapColumns([
        {
          title: '',
          field: 'entity_id',
          headerSort: false,
          width: 36,
          frozen: true,
          formatter: () => `<input type="checkbox" style="cursor:pointer;accent-color:#12283F;">`,
        },
        ...this.resolvedFields.map(id => vm.colDef(id)).filter(Boolean),
      ]);
    },

    // Common Tabulator options for both modes. No pagination footer; each mode
    // sets its own vertical sizing (see initTable / serverOptions).
    baseOptions() {
      return {
        layout: 'fitDataStretch',
        renderVerticalBuffer: 300,
        movableColumns: true,
        selectableRows: true,
        // entity_id is unique per unit (and per synthesized history child), so
        // it doubles as Tabulator's row index — required for getRow() lookups
        // when history children are attached after the row is rendered.
        index: 'entity_id',
        // Each MLS row can carry a `_children` array of price-history snapshots
        // (see MLSList.getMLSDetail). dataTree renders them as expandable child
        // rows nested under the project (name) column.
        dataTree: true,
        dataTreeElementColumn: 'name',
        dataTreeStartExpanded: true,
        dataTreeChildIndent: 16,
        columns: this.buildColumns(),
      };
    },

    // Server-side infinite scroll: fetch pages from the paged endpoint as the
    // user scrolls (offset advances by limit), with sort resolved server-side.
    serverOptions() {
      const vm = this;
      return {
        ...this.baseOptions(),
        height: '70vh',
        // NOTE: ajaxURL is intentionally NOT set here. Setting it makes
        // Tabulator auto-fetch on build — but the base filter query arrives a
        // tick later (parent mounts after this child), which would cause a
        // wasted first request with an empty query. We trigger the single load
        // via loadServer() once serverQuery is ready instead.
        progressiveLoad: 'scroll',
        progressiveLoadScrollMargin: 100,
        // 200 = endpoint's max limit; fewer round-trips over the ~10k rows.
        paginationSize: 200,
        sortMode: 'remote',
        // Tabulator sends page/size/sort; the endpoint wants limit/offset/sort_by.
        ajaxURLGenerator(url, config, params) {
          const size = params.size || 200;
          const page = params.page || 1;
          const offset = (page - 1) * size;
          let q = `${vm.serverQuery}limit=${size}&offset=${offset}`;
          if (params.sort && params.sort.length) {
            const key = SORT_KEY[params.sort[0].field];
            if (key) q += `&sort_by=${key}&sort_dir=${params.sort[0].dir}`;
          }
          return `${url}?${q}`;
        },
        // Unwrap { data, pagination } into Tabulator's { data, last_page } and
        // enrich each raw row, applying the same exclusions as the full load.
        // last_page is derived from total/limit so progressive scroll knows
        // when to stop requesting further pages.
        ajaxResponse(url, params, response) {
          const rows = ((response && response.data) || [])
            .filter(r => r.price_backup !== 'Educated Estimated' && r.price_date && r.price_date.length > 0 && !r.ignore_mls)
            .map(r => (vm.rowMapper ? vm.rowMapper(r) : r));
          // For now: only load the first page (offset 0). Reporting last_page: 1
          // stops progressive scroll from requesting further offsets. To restore
          // full infinite scroll, use Math.ceil(pagination.total / pagination.limit).
          return { data: rows, last_page: 1 };
        },
      };
    },

    initTable() {
      const vm = this;
      // Local (filtered) mode: maxHeight keeps short result sets compact while
      // still scrolling large ones via the virtual DOM. Server mode uses a
      // fixed height (see serverOptions) for correct progressive-scroll paging.
      const options = this.serverMode
        ? this.serverOptions()
        : { ...this.baseOptions(), data: this.rowData, maxHeight: '70vh' };
      this.tableBuilt = false;
      // The build below already uses the current fields and rowData, so any
      // pending replay from a mid-build prop change is stale.
      this._columnsDirty = false;
      this._dataDirty = false;
      this.tabulator = new Tabulator(this.$refs.table, options);
      this.tabulator.on('rowClick', (e, row) => {
        if (e.target && e.target.closest('a, input, button, .tabulator-data-tree-control')) return;
        // Don't open the history modal for the synthesized history child rows.
        const d = row.getData();
        if (d && d._isHistory) return;
        vm.$emit('row-click', d);
      });
      // setData before the table finishes building throws in Tabulator 6, and
      // the base query arrives a tick after this child mounts. Gate the single
      // fetch on tableBuilt so it fires once, whichever arrives last.
      this.tabulator.on('tableBuilt', () => {
        this.tableBuilt = true;
        // Apply prop changes that arrived while the table was building.
        if (this._columnsDirty) {
          this._columnsDirty = false;
          this.setColumns(this.resolvedFields);
        }
        if (this._dataDirty) {
          this._dataDirty = false;
          if (!this.serverMode) this.tabulator.replaceData(this.rowData || []);
        }
        this.loadServer();
        this.attachScrollListener();
      });
    },

    // Native scroll listener on Tabulator's scroll container. More reliable than
    // the library's scrollVertical event, which doesn't always fire. Emits
    // 'reached-last-row' once per arrival at the bottom (edge flag prevents a
    // flood while sitting at the last row).
    // Tabulator's scroll container. rowManager.element is the holder; fall back
    // to the DOM class (note: lowercase "tableholder" in Tabulator 6).
    scrollHolder() {
      if (this.tabulator && this.tabulator.rowManager && this.tabulator.rowManager.element) {
        return this.tabulator.rowManager.element;
      }
      return this.$el ? this.$el.querySelector('.tabulator-tableholder, .tabulator-tableHolder') : null;
    },

    attachScrollListener() {
      const holder = this.scrollHolder();
      if (!holder) return;
      if (this._scrollHolder) this._scrollHolder.removeEventListener('scroll', this._onScroll);
      this._onScroll = () => {
        const atEnd = this.isAtLastRow();
        if (atEnd && !this._reachedEnd) {
          this._reachedEnd = true;
          this.$emit('reached-last-row');
        } else if (!atEnd) {
          this._reachedEnd = false;
        }
      };
      holder.addEventListener('scroll', this._onScroll, { passive: true });
      this._scrollHolder = holder;
    },

    // Fires exactly one request to the paged endpoint (offset 0, 200 rows).
    // No-ops until the table is built and a query is present; the serverQuery
    // watcher and the tableBuilt handler both route through here, so whichever
    // is ready last triggers the single load.
    loadServer() {
      if (this.tabulator && this.tableBuilt && this.serverMode && this.serverQuery) {
        this.tabulator.setData(this.serverUrl);
      }
    },

    // Returns true when the table body is scrolled to the last row. `threshold`
    // is a px tolerance so it triggers slightly before the exact bottom (handy
    // for pre-fetching the next page). Reads the scrollable tableHolder that
    // Tabulator renders inside our root element.
    isAtLastRow(threshold = 8) {
      const holder = this.scrollHolder();
      if (!holder) return false;
      // No overflow yet (fewer rows than the viewport) -> the last row is shown.
      if (holder.scrollHeight <= holder.clientHeight) return true;
      return holder.scrollTop + holder.clientHeight >= holder.scrollHeight - threshold;
    },

    rebuildTable() {
      if (this.tabulator) {
        this.tabulator.destroy();
        this.tabulator = null;
      }
      this.initTable();
    },

    // Attaches price-history snapshots as expandable tree children to an
    // already-rendered row. Called by the parent as each row's history request
    // resolves (see MLSList.fetchHistoriesForRows). addTreeChild writes the
    // children onto the row's own data object (`_children`) — which, since
    // Tabulator holds the row data by reference, is the same object the parent
    // keeps in mlsData, so a later replaceData (filter/sort) rebuilds them for
    // free. Returns false when the row isn't currently rendered so the caller
    // can persist the children itself. Does NOT pre-assign _children — that
    // would make addTreeChild push onto an already-populated array (duplicates).
    addRowChildren(entityId, children) {
      if (!this.tabulator || !this.tableBuilt || !children || !children.length) return false;
      let row;
      try {
        row = this.tabulator.getRow(entityId);
      } catch (e) {
        return false; // row not currently rendered (e.g. filtered out)
      }
      if (!row) return false;
      children.forEach(child => row.addTreeChild(child));
      return true;
    },
  },
};
</script>

<style>
/* ── Wrapper ── */
.mls-tab-wrap .tabulator {
  border: none !important;
  font-family: Helvetica, Arial, sans-serif !important;
  font-size: 12px !important;
  color: #12283F !important;
}

/* ── Header ── */
.mls-tab-wrap .tabulator-header {
  background: #F7F8FB !important;
  border-bottom: 1px solid #E2E4EA !important;
}

.mls-tab-wrap .tabulator-col-title {
  font-family: Poppins, sans-serif !important;
  font-weight: 600 !important;
  font-size: 10px !important;
  color: #545454 !important;
  text-transform: uppercase !important;
  letter-spacing: 0.08em !important;
}

/* ── Rows ── */
.mls-tab-wrap .tabulator-row {
  border-bottom: 1px solid #F0F1F4 !important;
}

.mls-tab-wrap .tabulator-row:hover {
  background: #FAFBFD !important;
}

.mls-tab-wrap .tabulator-row.tabulator-selected {
  background: #F4F7FE !important;
}

/* ── History (tree child) rows ── */
.mls-tab-wrap .tabulator-row.tabulator-tree-level-1,
.mls-tab-wrap .tabulator-row.tabulator-tree-level-2 {
  background: #FAFBFD !important;
}

.mls-tab-wrap .tabulator-row.tabulator-tree-level-1 .rpd-proj-name,
.mls-tab-wrap .tabulator-row.tabulator-tree-level-2 .rpd-proj-name {
  font-weight: 500;
  font-size: 11px;
  color: #8A8A8A;
}

.mls-tab-wrap .tabulator-cell {
  padding: 10px 14px !important;
  font-variant-numeric: tabular-nums !important;
}

/* ── Pagination ── */
.mls-tab-wrap .tabulator-footer {
  border-top: 1px solid #E2E4EA !important;
  background: #fff !important;
}

.mls-tab-wrap .tabulator-page {
  font-family: Helvetica, Arial, sans-serif !important;
  font-size: 12px !important;
  border: 1px solid #E2E4EA !important;
  border-radius: 6px !important;
  color: #545454 !important;
  margin: 0 2px !important;
}

.mls-tab-wrap .tabulator-page.active {
  background: #12283F !important;
  color: #fff !important;
  border-color: #12283F !important;
}

/* ── Cell helpers ── */
.rpd-proj-cell {
  display: flex;
  align-items: center;
  gap: 8px;
}

.rpd-proj-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.rpd-proj-name {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rpd-proj-dev {
  font-size: 11px;
  color: #8A8A8A;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rpd-loc-cell {
  display: flex;
  flex-direction: column;
  gap: 1px;
}

.rpd-loc-addr {
  font-size: 12px;
  color: #12283F;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.rpd-loc-city {
  font-size: 11px;
  color: #8A8A8A;
}

.rpd-unit-num {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 12px;
  color: #343376;
}

.rpd-type {
  font-size: 12px;
  color: #12283F;
  font-weight: 500;
}

.rpd-fp-spec {
  font-size: 10px;
  color: #8A8A8A;
}

.rpd-sf {
  color: #B6BAC2;
  font-size: 10px;
}

.rpd-price-cell {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
  justify-content: flex-end;
  width: 100%;
}

.rpd-price {
  font-family: Poppins, sans-serif;
  font-weight: 600;
  font-size: 13px;
  color: #12283F;
}

.rpd-chip {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  padding: 1px 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.rpd-chip--up {
  background: rgba(31, 157, 85, 0.12);
  color: #0F6B3A;
}

.rpd-chip--dn {
  background: rgba(212, 56, 71, 0.10);
  color: #A8323F;
}

.rpd-incent {
  padding: 2px 7px;
  background: #FFF6E5;
  color: #8A6A00;
  border-radius: 4px;
  font-size: 11px;
}

.rpd-nil {
  color: #C9CCD2;
}

.rpd-net {
  font-weight: 500;
}

.rpd-pill {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 3px 9px 3px 8px;
  border-radius: 999px;
  font-size: 11px;
  white-space: nowrap;
}

.rpd-pill-dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  flex-shrink: 0;
}

.rpd-new-badge {
  padding: 2px 7px;
  background: rgba(31, 157, 85, 0.10);
  color: #1F7A45;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.08em;
}

.rpd-vbadge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  color: #fff;
  flex-shrink: 0;
  font-size: 11px;
  font-weight: 700;
}

.rpd-vbadge--green {
  background: #1F9D55;
}

.rpd-vbadge--amber {
  background: #FFB81C;
}

.rpd-vbadge--empty {
  width: 18px;
  height: 18px;
  display: inline-block;
}
</style>
